import { useCallback, useState, useEffect } from "react";
import { AnimatePresence } from "framer-motion";
import { HandHeart, X, Loader2, Trash2, PhoneCall, ExternalLink, FileText, Check, Ban, RefreshCw, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { api, assetUrl, formatApiErrorDetail } from "@/lib/api";
import { AdminPartnerTerritory } from "@/components/AdminPartnerTerritory";
import { AdminCreatePartnerModal } from "@/components/AdminCreatePartnerModal";
import { AdminModalOverlay, AdminModalCard, AdminModalBody } from "@/components/AdminModal";

const STATUS_LABELS = {
  NOUVELLE_CANDIDATURE: "Nouvelle candidature",
  A_ETUDIER: "À étudier",
  PRE_VALIDEE: "Pré-validée",
  DOCUMENTS_A_FOURNIR: "Documents à fournir",
  DOSSIER_COMPLET: "Dossier complet",
  VALIDEE: "Validée",
  REFUSEE: "Refusée",
};

const STATUS_ORDER = Object.keys(STATUS_LABELS);

const STATUS_CLS = {
  NOUVELLE_CANDIDATURE: "bg-cognac/15 text-cognac-dark",
  A_ETUDIER: "bg-amber-100 text-amber-700",
  PRE_VALIDEE: "bg-sky-100 text-sky-700",
  DOCUMENTS_A_FOURNIR: "bg-orange-100 text-orange-700",
  DOSSIER_COMPLET: "bg-indigo-100 text-indigo-700",
  VALIDEE: "bg-sage/20 text-navy",
  REFUSEE: "bg-red-100 text-red-600",
};

const DOCUMENT_TYPES = ["identity", "kbis", "rc_pro"];
const DOCUMENT_LABELS = { identity: "Pièce d'identité", kbis: "KBIS", rc_pro: "Attestation RC Professionnelle" };
const DOC_STATUS_LABELS = { a_fournir: "À fournir", envoye: "Document envoyé", valide: "Validé", refuse: "Refusé" };
const DOC_STATUS_CLS = {
  a_fournir: "bg-slate-100 text-slate-500", envoye: "bg-sky-100 text-sky-700",
  valide: "bg-sage/20 text-navy", refuse: "bg-red-100 text-red-600",
};
const REJECT_REASONS = ["Document illisible", "Document expiré", "Document incomplet", "Mauvais document", "Autre"];

const CHECK_TYPES = ["entreprise", "profil"];
const CHECK_LABELS = { entreprise: "Entreprise", profil: "Profil candidat" };
const CHECK_STATUS_LABELS = { conforme: "Conforme", a_examiner: "À examiner", bloquant: "Bloquant" };
const CHECK_STATUS_CLS = {
  conforme: "bg-sage/20 text-navy", a_examiner: "bg-amber-100 text-amber-700", bloquant: "bg-red-100 text-red-600",
};

const fmtDate = (iso) =>
  new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

const SITUATION_LABELS = {
  installe: "Déjà installé / entreprise existante",
  en_creation: "Entreprise en cours de création",
  a_creer: "Souhaite créer son activité",
};

const EXPERIENCE_LABELS = {
  aucune: "Aucune", moins_2_ans: "Moins de 2 ans", "2_5_ans": "2 à 5 ans",
  "5_10_ans": "5 à 10 ans", plus_10_ans: "Plus de 10 ans",
};

export const AdminPartnerApplications = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [rejecting, setRejecting] = useState(null); // { docType, reason, custom }
  const [busyDoc, setBusyDoc] = useState(null);
  const [regenerating, setRegenerating] = useState(false);

  const fetchApplications = useCallback(() => {
    api.get("/admin/partner-applications")
      .then((res) => setApplications(res.data))
      .catch(() => toast.error("Impossible de charger les candidatures"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchApplications(); }, [fetchApplications]);

  const nouvelles = applications.filter((a) => a.status === "NOUVELLE_CANDIDATURE").length;

  const changeStatus = async (app, status) => {
    try {
      const res = await api.patch(`/admin/partner-applications/${app.id}`, { status });
      toast.success(`Statut mis à jour : ${STATUS_LABELS[status]}`);
      if (status === "VALIDEE") {
        if (res.data.intended_unavailable) {
          toast.warning("Le secteur souhaité par le candidat n'est plus disponible — un secteur a été recalculé automatiquement.");
        } else if (res.data.territory_proposed === false) {
          toast.warning("Aucun secteur n'a pu être identifié automatiquement — à attribuer manuellement.");
        }
      }
      refreshDetail(app.id);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur lors de la mise à jour du statut");
    }
  };

  const [busyCheck, setBusyCheck] = useState(null);
  const updateCheck = async (app, checkType, status) => {
    setBusyCheck(checkType);
    try {
      await api.patch(`/admin/partner-applications/${app.id}/checks/${checkType}`, { status });
      toast.success(`${CHECK_LABELS[checkType]} : ${CHECK_STATUS_LABELS[status]}`);
      refreshDetail(app.id);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusyCheck(null);
    }
  };

  const remove = async (app) => {
    if (!window.confirm(`Supprimer la candidature de ${app.firstname} ${app.lastname} ?`)) return;
    try {
      await api.delete(`/admin/partner-applications/${app.id}`);
      toast.success("Candidature supprimée");
      setDetail(null);
      fetchApplications();
    } catch {
      toast.error("Erreur lors de la suppression");
    }
  };

  const refreshDetail = (id) =>
    api.get("/admin/partner-applications").then((res) => {
      setApplications(res.data);
      setDetail(res.data.find((a) => a.id === id) || null);
    });

  const validateDocument = async (app, docType) => {
    setBusyDoc(docType);
    try {
      await api.patch(`/admin/partner-applications/${app.id}/documents/${docType}`, { action: "valider" });
      toast.success(`${DOCUMENT_LABELS[docType]} validé`);
      refreshDetail(app.id);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusyDoc(null);
    }
  };

  const confirmReject = async (app) => {
    const reason = rejecting.reason === "Autre" ? rejecting.custom.trim() : rejecting.reason;
    if (!reason) {
      toast.error("Merci de préciser un motif de refus");
      return;
    }
    setBusyDoc(rejecting.docType);
    try {
      await api.patch(`/admin/partner-applications/${app.id}/documents/${rejecting.docType}`, { action: "refuser", reason });
      toast.success(`${DOCUMENT_LABELS[rejecting.docType]} refusé`);
      setRejecting(null);
      refreshDetail(app.id);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusyDoc(null);
    }
  };

  const regenerateLink = async (app) => {
    setRegenerating(true);
    try {
      await api.post(`/admin/partner-applications/${app.id}/regenerate-link`);
      toast.success("Nouveau lien sécurisé envoyé au candidat");
    } catch {
      toast.error("Erreur lors de la régénération du lien");
    } finally {
      setRegenerating(false);
    }
  };

  const [showCreate, setShowCreate] = useState(false);

  if (loading) {
    return <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-navy" /></div>;
  }

  return (
    <div data-testid="admin-partner-applications">
      <div className="flex items-center justify-end">
        <button
          data-testid="admin-create-partner-open"
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 rounded-full bg-navy px-5 py-2.5 text-sm font-bold text-white transition-colors duration-300 hover:bg-cognac"
        >
          <UserPlus className="h-4 w-4" /> Ajouter un partenaire
        </button>
      </div>
      <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3">
        <div data-testid="admin-partner-stat-total" className="rounded-2xl border border-slate-200 bg-white p-5">
          <HandHeart className="h-5 w-5 text-cognac" />
          <p className="mt-3 font-display text-3xl font-semibold text-navy">{applications.length}</p>
          <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Total</p>
        </div>
        <div data-testid="admin-partner-stat-new" className="rounded-2xl border border-slate-200 bg-white p-5">
          <HandHeart className="h-5 w-5 text-cognac" />
          <p className="mt-3 font-display text-3xl font-semibold text-navy">{nouvelles}</p>
          <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Nouvelles</p>
        </div>
      </div>

      <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-white">
        {applications.length === 0 ? (
          <p data-testid="admin-partner-empty" className="py-16 text-center text-sm text-slate-400">
            Aucune candidature pour le moment.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[820px] text-left text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-xs uppercase tracking-wider text-slate-400">
                  <th className="px-5 py-4">Reçu le</th>
                  <th className="px-5 py-4">Candidat</th>
                  <th className="px-5 py-4">Secteur</th>
                  <th className="px-5 py-4">Département</th>
                  <th className="px-5 py-4">Statut</th>
                  <th className="px-5 py-4" />
                </tr>
              </thead>
              <tbody>
                {applications.map((a) => (
                  <tr
                    key={a.id}
                    data-testid={`admin-partner-row-${a.id}`}
                    onClick={() => setDetail(a)}
                    className="cursor-pointer border-b border-slate-100 transition-colors duration-200 hover:bg-cream"
                  >
                    <td className="whitespace-nowrap px-5 py-4 text-slate-500">{fmtDate(a.created_at)}</td>
                    <td className="px-5 py-4">
                      <p className="font-bold text-navy">{a.firstname} {a.lastname}</p>
                      <p className="text-xs text-slate-400">{a.phone}</p>
                    </td>
                    <td className="px-5 py-4 text-slate-600">{a.sector}</td>
                    <td className="px-5 py-4 text-slate-600">{a.department}</td>
                    <td className="px-5 py-4">
                      <span data-testid={`admin-partner-status-${a.id}`} className={`rounded-full px-3 py-1 text-xs font-bold ${STATUS_CLS[a.status]}`}>
                        {STATUS_LABELS[a.status]}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-right text-slate-300">
                      <ExternalLink className="ml-auto h-4 w-4" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <AnimatePresence>
        {detail && (
          <AdminModalOverlay testId="admin-partner-detail-modal" onClose={() => setDetail(null)}>
            <AdminModalCard>
              <div className="flex shrink-0 items-start justify-between border-b border-slate-100 px-7 py-6 sm:px-9">
                <div>
                  <h2 className="font-display text-2xl font-medium text-navy">{detail.firstname} {detail.lastname}</h2>
                  <p className="mt-1 text-xs text-slate-400">{fmtDate(detail.created_at)}</p>
                </div>
                <button
                  data-testid="admin-partner-detail-close"
                  onClick={() => setDetail(null)}
                  className="rounded-full border border-slate-200 p-2 text-slate-500 transition-colors duration-300 hover:bg-navy hover:text-white"
                  aria-label="Fermer"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <AdminModalBody>
              <dl className="space-y-3 text-sm">
                {[
                  ["Téléphone", detail.phone],
                  ["Email", detail.email],
                  ["Situation professionnelle", SITUATION_LABELS[detail.situation] || detail.situation],
                  detail.company_name && ["Entreprise", detail.company_name],
                  detail.siret && ["SIRET", detail.siret],
                  ["Ville", detail.city],
                  ["Code postal", detail.postal_code],
                  ["Département", detail.department],
                  ["Secteur souhaité", detail.sector],
                  ["Déjà maître d'œuvre", detail.already_moe ? "Oui" : "Non"],
                  ["Expérience", EXPERIENCE_LABELS[detail.experience] || detail.experience],
                ].filter(Boolean).map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-4 border-b border-slate-100 pb-3">
                    <dt className="shrink-0 text-slate-400">{k}</dt>
                    <dd className="text-right font-semibold text-navy">{v}</dd>
                  </div>
                ))}
                {detail.message && (
                  <div data-testid="admin-partner-detail-message" className="rounded-xl bg-cream p-4 text-slate-600">{detail.message}</div>
                )}
              </dl>

              <div className="mt-6">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Décision Centrale</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  <button
                    type="button"
                    data-testid="admin-partner-quick-valider"
                    onClick={() => changeStatus(detail, "VALIDEE")}
                    disabled={detail.status === "VALIDEE"}
                    className="flex items-center gap-1.5 rounded-full bg-sage/20 px-4 py-2 text-xs font-bold text-navy transition-colors hover:bg-sage/30 disabled:opacity-40"
                  >
                    <Check className="h-3.5 w-3.5" /> Valider
                  </button>
                  <button
                    type="button"
                    data-testid="admin-partner-quick-complement"
                    onClick={() => changeStatus(detail, "DOCUMENTS_A_FOURNIR")}
                    disabled={detail.status === "DOCUMENTS_A_FOURNIR"}
                    className="flex items-center gap-1.5 rounded-full bg-orange-50 px-4 py-2 text-xs font-bold text-orange-700 transition-colors hover:bg-orange-100 disabled:opacity-40"
                  >
                    <RefreshCw className="h-3.5 w-3.5" /> Demander un complément
                  </button>
                  <button
                    type="button"
                    data-testid="admin-partner-quick-refuser"
                    onClick={() => changeStatus(detail, "REFUSEE")}
                    disabled={detail.status === "REFUSEE"}
                    className="flex items-center gap-1.5 rounded-full bg-red-50 px-4 py-2 text-xs font-bold text-red-500 transition-colors hover:bg-red-100 disabled:opacity-40"
                  >
                    <Ban className="h-3.5 w-3.5" /> Refuser
                  </button>
                </div>
              </div>

              <div data-testid="admin-partner-checks" className="mt-6">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Vérifications entreprise / profil</p>
                <div className="mt-2 space-y-2">
                  {CHECK_TYPES.map((ct) => {
                    const check = detail.checks?.[ct] || { status: "a_examiner" };
                    return (
                      <div key={ct} data-testid={`admin-check-${ct}`} className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 p-3">
                        <span className="text-sm font-bold text-navy">{CHECK_LABELS[ct]}</span>
                        <select
                          data-testid={`admin-check-select-${ct}`}
                          value={check.status}
                          disabled={busyCheck === ct}
                          onChange={(e) => updateCheck(detail, ct, e.target.value)}
                          className={`rounded-full border-0 px-3 py-1.5 text-xs font-bold focus:outline-none ${CHECK_STATUS_CLS[check.status]}`}
                        >
                          {Object.keys(CHECK_STATUS_LABELS).map((s) => (
                            <option key={s} value={s}>{CHECK_STATUS_LABELS[s]}</option>
                          ))}
                        </select>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="mt-6">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Autre statut du dossier</p>
                <select
                  data-testid="admin-partner-status-select"
                  value={detail.status}
                  onChange={(e) => changeStatus(detail, e.target.value)}
                  className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-navy focus:border-cognac focus:outline-none"
                >
                  {STATUS_ORDER.map((s) => (
                    <option key={s} value={s}>{STATUS_LABELS[s]}</option>
                  ))}
                </select>
              </div>

              {detail.documents && (
                <div data-testid="admin-partner-documents" className="mt-7">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Documents</p>
                    <button
                      type="button"
                      data-testid="admin-partner-regenerate-link"
                      onClick={() => regenerateLink(detail)}
                      disabled={regenerating}
                      className="flex items-center gap-1.5 text-xs font-bold text-cognac transition-colors hover:text-navy disabled:opacity-50"
                    >
                      {regenerating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                      Renvoyer le lien sécurisé
                    </button>
                  </div>
                  <div className="mt-3 space-y-3">
                    {DOCUMENT_TYPES.map((docType) => {
                      const doc = detail.documents[docType] || {};
                      return (
                        <div key={docType} data-testid={`admin-doc-${docType}`} className="rounded-xl border border-slate-200 p-4">
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <div className="flex items-center gap-2">
                              <FileText className="h-4 w-4 text-slate-400" />
                              <p className="text-sm font-bold text-navy">{DOCUMENT_LABELS[docType]}</p>
                              {!doc.blocking && <span className="text-[10px] font-bold uppercase text-slate-400">(non bloquant)</span>}
                            </div>
                            <span data-testid={`admin-doc-status-${docType}`} className={`rounded-full px-2.5 py-1 text-xs font-bold ${DOC_STATUS_CLS[doc.status] || DOC_STATUS_CLS.a_fournir}`}>
                              {DOC_STATUS_LABELS[doc.status] || "À fournir"}
                            </span>
                          </div>
                          {doc.uploaded_at && (
                            <p className="mt-1.5 text-xs text-slate-400">Envoyé le {fmtDate(doc.uploaded_at)}</p>
                          )}
                          {doc.status === "refuse" && doc.rejection_reason && (
                            <p className="mt-1.5 text-xs text-red-500">Motif : {doc.rejection_reason}</p>
                          )}
                          <div className="mt-3 flex flex-wrap items-center gap-2">
                            {doc.path && (
                              <a
                                data-testid={`admin-doc-view-${docType}`}
                                href={assetUrl(`/api/admin/partner-applications/${detail.id}/documents/${docType}/file`)}
                                target="_blank"
                                rel="noreferrer"
                                className="flex items-center gap-1.5 rounded-full border border-slate-200 px-3.5 py-1.5 text-xs font-bold text-navy transition-colors hover:border-cognac hover:text-cognac"
                              >
                                <ExternalLink className="h-3.5 w-3.5" /> Consulter
                              </a>
                            )}
                            {doc.status === "envoye" && (
                              <>
                                <button
                                  type="button"
                                  data-testid={`admin-doc-validate-${docType}`}
                                  onClick={() => validateDocument(detail, docType)}
                                  disabled={busyDoc === docType}
                                  className="flex items-center gap-1.5 rounded-full bg-sage/20 px-3.5 py-1.5 text-xs font-bold text-navy transition-colors hover:bg-sage/30 disabled:opacity-50"
                                >
                                  <Check className="h-3.5 w-3.5" /> Valider
                                </button>
                                <button
                                  type="button"
                                  data-testid={`admin-doc-reject-${docType}`}
                                  onClick={() => setRejecting({ docType, reason: REJECT_REASONS[0], custom: "" })}
                                  disabled={busyDoc === docType}
                                  className="flex items-center gap-1.5 rounded-full bg-red-50 px-3.5 py-1.5 text-xs font-bold text-red-500 transition-colors hover:bg-red-100 disabled:opacity-50"
                                >
                                  <Ban className="h-3.5 w-3.5" /> Refuser
                                </button>
                              </>
                            )}
                          </div>
                          {rejecting?.docType === docType && (
                            <div data-testid={`admin-doc-reject-form-${docType}`} className="mt-3 rounded-xl bg-cream p-3.5">
                              <select
                                data-testid={`admin-doc-reject-reason-${docType}`}
                                value={rejecting.reason}
                                onChange={(e) => setRejecting({ ...rejecting, reason: e.target.value })}
                                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-navy focus:border-cognac focus:outline-none"
                              >
                                {REJECT_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
                              </select>
                              {rejecting.reason === "Autre" && (
                                <input
                                  data-testid={`admin-doc-reject-custom-${docType}`}
                                  value={rejecting.custom}
                                  onChange={(e) => setRejecting({ ...rejecting, custom: e.target.value })}
                                  placeholder="Précisez le motif"
                                  className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-xs focus:border-cognac focus:outline-none"
                                />
                              )}
                              <div className="mt-2.5 flex gap-2">
                                <button
                                  type="button"
                                  data-testid={`admin-doc-reject-confirm-${docType}`}
                                  onClick={() => confirmReject(detail)}
                                  disabled={busyDoc === docType}
                                  className="rounded-full bg-red-500 px-4 py-1.5 text-xs font-bold text-white transition-colors hover:bg-red-600 disabled:opacity-50"
                                >
                                  Confirmer le refus
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setRejecting(null)}
                                  className="rounded-full border border-slate-200 px-4 py-1.5 text-xs font-bold text-slate-500"
                                >
                                  Annuler
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {detail.partner && <AdminPartnerTerritory partner={detail.partner} onUpdated={() => refreshDetail(detail.id)} />}
              </AdminModalBody>

              <div className="flex shrink-0 flex-wrap gap-3 border-t border-slate-100 px-7 py-5 sm:px-9">
                <a
                  data-testid="admin-partner-detail-call-button"
                  href={`tel:${detail.phone}`}
                  className="flex flex-1 items-center justify-center gap-2 rounded-full bg-navy px-5 py-3.5 text-sm font-bold text-white transition-colors duration-300 hover:bg-cognac"
                >
                  <PhoneCall className="h-4 w-4" /> Appeler
                </a>
                <button
                  data-testid="admin-partner-detail-delete"
                  onClick={() => remove(detail)}
                  className="rounded-full border border-red-200 p-3.5 text-red-400 transition-colors duration-300 hover:bg-red-500 hover:text-white"
                  aria-label="Supprimer"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </AdminModalCard>
          </AdminModalOverlay>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showCreate && (
          <AdminCreatePartnerModal
            onClose={() => setShowCreate(false)}
            onCreated={() => { setShowCreate(false); fetchApplications(); }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};
